export type MessageSeriesPosition = 'single' | 'first' | 'middle' | 'last'

export interface MessageSeriesMeta {
  position: MessageSeriesPosition
  isFirstInSeries: boolean
  isLastInSeries: boolean
  showSenderHeader: boolean
}

const SERIES_THRESHOLD_MS = 60 * 1000

type SeriesMessage = {
  createdAt: string
  sender: { id: number }
  isSystem?: boolean
}

const canGroupMessages = (later: SeriesMessage, earlier: SeriesMessage | null | undefined): boolean => {
  if (!earlier) return false
  if (later.isSystem || earlier.isSystem) return false
  if (later.sender.id !== earlier.sender.id) return false

  const timeDiffMs = new Date(later.createdAt).getTime() - new Date(earlier.createdAt).getTime()
  return timeDiffMs >= 0 && timeDiffMs < SERIES_THRESHOLD_MS
}

export const getMessageSeriesMeta = (messages: SeriesMessage[], index: number): MessageSeriesMeta => {
  const current = messages[index]

  if (!current || current.isSystem) {
    return {
      position: 'single',
      isFirstInSeries: true,
      isLastInSeries: true,
      showSenderHeader: true,
    }
  }

  const groupsWithPrevious = canGroupMessages(current, index > 0 ? messages[index - 1] : null)
  const groupsWithNext =
    index < messages.length - 1 && canGroupMessages(messages[index + 1], current)

  let position: MessageSeriesPosition
  if (!groupsWithPrevious && !groupsWithNext) {
    position = 'single'
  } else if (!groupsWithPrevious && groupsWithNext) {
    position = 'first'
  } else if (groupsWithPrevious && groupsWithNext) {
    position = 'middle'
  } else {
    position = 'last'
  }

  return {
    position,
    isFirstInSeries: position === 'single' || position === 'first',
    isLastInSeries: position === 'single' || position === 'last',
    showSenderHeader: position === 'single' || position === 'first',
  }
}

export const groupMessageIndices = (messages: SeriesMessage[]): number[][] => {
  if (messages.length === 0) return []

  const groups: number[][] = [[0]]

  for (let index = 1; index < messages.length; index++) {
    const meta = getMessageSeriesMeta(messages, index)
    if (meta.isFirstInSeries) {
      groups.push([index])
    } else {
      groups[groups.length - 1].push(index)
    }
  }

  return groups
}

export const getMessageSeriesGroupClassName = (isOwnMessage: boolean): string => {
  return `flex w-full flex-col gap-1 ${isOwnMessage ? 'items-end' : 'items-start'}`
}

export const getMessageSeriesBubblePaddingClassName = (position: MessageSeriesPosition): string => {
  switch (position) {
    case 'first':
      return 'pb-1'
    case 'middle':
      return 'py-1'
    case 'last':
      return 'pt-1'
    default:
      return ''
  }
}

export const getSplitAttachmentSeriesPosition = (position: MessageSeriesPosition): MessageSeriesPosition => {
  if (position === 'single' || position === 'first') return 'first'
  return 'middle'
}

export const getSplitTextSeriesPosition = (position: MessageSeriesPosition): MessageSeriesPosition => {
  if (position === 'single' || position === 'last') return 'last'
  return 'middle'
}

export const getMessageSeriesBubbleClassName = (
  position: MessageSeriesPosition,
  isOwnMessage: boolean
): string => {
  if (position === 'single') return 'rounded-lg'

  if (isOwnMessage) {
    switch (position) {
      case 'first':
        return 'rounded-t-lg rounded-l-lg rounded-br-sm'
      case 'middle':
        return 'rounded-l-lg rounded-tr-sm rounded-br-sm'
      case 'last':
        return 'rounded-b-lg rounded-l-lg rounded-tr-sm'
      default:
        return 'rounded-lg'
    }
  }

  switch (position) {
    case 'first':
      return 'rounded-t-lg rounded-r-lg rounded-bl-sm'
    case 'middle':
      return 'rounded-r-lg rounded-tl-sm rounded-bl-sm'
    case 'last':
      return 'rounded-b-lg rounded-r-lg rounded-tl-sm'
    default:
      return 'rounded-lg'
  }
}
